// Pure helpers for a receipt that is SHORT of what was expected. No Firestore.
//
// Expected cash for an invoice = what is still outstanding on it less the WHT
// and GST the client is recognised as having withheld in this receipt. If the
// client pays less than the total of that, finance must decide: leave the rest
// outstanding (more is coming) or accept a client adjustment, which is booked
// as a direct cost of that client and clears the receivable.
import { round } from "./whtCalculator";

export const SHORTFALL_TOLERANCE = 5; // same tolerance the AR register uses for "reconciled"

export const cashExpectedFor = (outstanding, whtRecognized = 0, gstWithheldRecognized = 0) =>
  Math.max(0, round(Number(outstanding || 0) - Number(whtRecognized || 0) - Number(gstWithheldRecognized || 0)));

// selections: [{ cashExpected }]
export function shortfallSummary({ selections, receivedAmount }) {
  const expectedTotal = round(selections.reduce((sum, selection) => sum + Number(selection.cashExpected || 0), 0));
  const received = round(receivedAmount);
  const shortfall = Math.max(0, expectedTotal - received);
  return { expectedTotal, received, shortfall, isShort: shortfall > SHORTFALL_TOLERANCE };
}

// After the received amount has been allocated: how much each invoice is still short.
export const allocationShortfalls = (selections) =>
  selections.map((selection) => Math.max(0, round(Number(selection.cashExpected || 0) - Number(selection.allocatedAmount || 0))));

// Spreads an adjustment across the invoices that are short, in proportion to how
// short each one is. Each share is capped at that invoice's shortfall and the
// shares always add up to exactly the adjustment.
export function splitAdjustment(adjustment, shortfalls) {
  const amount = round(adjustment);
  const total = shortfalls.reduce((sum, value) => sum + value, 0);
  if (amount > total) throw new Error(`The adjustment (PKR ${amount}) is more than the shortfall on the selected invoices (PKR ${total}).`);
  if (amount <= 0 || total <= 0) return shortfalls.map(() => 0);
  const shares = shortfalls.map((value) => Math.min(value, Math.floor((amount * value) / total)));
  let remainder = amount - shares.reduce((sum, value) => sum + value, 0);
  for (let index = 0; remainder > 0 && index < shares.length; index += 1) {
    const room = shortfalls[index] - shares[index];
    const add = Math.min(room, remainder);
    shares[index] += add;
    remainder -= add;
  }
  return shares;
}

export function validateAdjustment({ amount, note, accountingPeriod, shortfall }) {
  if (!(round(amount) > 0)) return "Enter the adjustment amount.";
  if (round(amount) > round(shortfall)) return `The adjustment cannot be more than the shortfall (PKR ${round(shortfall).toLocaleString("en-PK")}).`;
  if (!String(note || "").trim()) return "Enter a note explaining the client adjustment.";
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(String(accountingPeriod || ""))) return "Choose the month the adjustment is booked in.";
  return "";
}
